/**
 * Renders an {@link AudienceArtifactCard} from a parsed `audience_artifact` transcript entry, resolving the
 * one thing the card itself has no business knowing: where "Open audience" goes.
 *
 * The audience MCP tool (`save_audience_draft`) emits these on the generic `ui_message` event bus — NOT the
 * sidecar-adoption path `dashboard_artifact` uses — so they arrive as plain ui_messages. Parsing lives in
 * `chat/lib/chat-artifacts.ts` so the inline card and the pinned copy cannot disagree about a payload, and
 * so the pinning rule can be tested without rendering anything.
 */
import { useNavigate } from 'react-router-dom';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { AudienceArtifactCard } from '@/audiences/components/AudienceArtifactCard';
import { audienceHref } from '@/audiences/lib/audience-nav';

import type { AudienceChatArtifact } from '../../lib/chat-artifacts';

export interface ChatAudienceArtifactProps {
  artifact: AudienceChatArtifact;
  variant?: 'default' | 'compact';
}

export function ChatAudienceArtifact({ artifact, variant = 'default' }: ChatAudienceArtifactProps): React.ReactElement {
  const { wp } = useWorkspace();
  const navigate = useNavigate();
  const { audienceId } = artifact;

  return (
    <AudienceArtifactCard
      title={artifact.title}
      status={artifact.status}
      countState={artifact.countState}
      variant={variant}
      // No id means a malformed payload: the card then shows the CTA disabled rather than a dead link.
      {...(audienceId === null ? {} : { onOpen: () => void navigate(audienceHref(wp, audienceId)) })}
    />
  );
}
